/**
 * Экран результата диагностики (ТЗ FR-EN-61, FR-EN-62).
 *
 * Диагностика измеряла уровень и молчала о нём: baseline записывался,
 * тренировка по нему строилась, а человек уходил в занятие, так и не узнав,
 * что про него выяснили. Экран отвечает на три вопроса: какой уровень, что
 * даётся хуже всего и что делать дальше.
 *
 * Результат не запирает ничего. Он может быть неверным — например, если
 * клавиши искали глазами, — поэтому рядом стоит «пройти заново», а
 * рекомендация остаётся рекомендацией: обе кнопки продолжения доступны всегда
 * (то же решение, что и с закрытыми режимами, — ТЗ FR-EN-55).
 *
 * Порядок на экране: сначала измеренное и следующий шаг, потом разбор
 * (ТЗ UI-39, UI-40). Тепловая карта отвечает на «что именно хромает» — вопрос
 * второй; первый — «что мне теперь делать». Определения метрик раскрываются по
 * требованию: их читают один раз.
 */

import type { Translate } from '@type2x/i18n';
import { MIN_RELIABLE_KEYSTROKES, type DiagnosticResult } from '@type2x/feature-diagnostics';
import { Button, Card, HeatMap, MetricsBar } from '@type2x/ui';

export interface DiagnosticResultScreenProps {
  readonly t: Translate;
  readonly diagnostic: DiagnosticResult;
  /** Перейти к персональной тренировке. */
  readonly onContinue: () => void;
  /** Начать с основ: постановка рук нужна не только самым медленным. */
  readonly onStartFundamentals: () => void;
  readonly onRetake: () => void;
}

export function DiagnosticResultScreen({
  t,
  diagnostic,
  onContinue,
  onStartFundamentals,
  onRetake,
}: DiagnosticResultScreenProps): React.JSX.Element {
  const { level, metrics, weakSpots } = diagnostic;
  const advised = level.recommendation;

  return (
    <Card title={t('diagnosticResult.title')}>
      {/* Уровень назван словом и числом сразу: одно слово ничего не говорит о
          том, насколько до следующей ступени, а одно число — о том, много это
          или мало. */}
      <p className="t2x-level">
        {t(`skillLevel.${level.level}`)}
        {' · '}
        {t('skillLevel.effectiveCpm', { cpm: Math.round(level.effectiveCpm) })}
      </p>
      <p className="t2x-muted">{t(`diagnosticResult.explain.${level.level}`)}</p>

      {/* Сколько нажатий за этим стоит. Малая выборка называется вслух: числа
          при ней скорее случайны, чем измерены (ТЗ UI-39). */}
      <p className="t2x-muted t2x-diagnostic__sample">
        {metrics.keystrokes < MIN_RELIABLE_KEYSTROKES
          ? t('diagnosticResult.sampleLimited', { count: metrics.keystrokes })
          : t('diagnosticResult.analysed', { count: metrics.keystrokes })}
      </p>

      <MetricsBar
        metrics={metrics}
        labels={{
          wpm: t('metrics.wpm'),
          cpm: t('metrics.cpm'),
          accuracy: t('metrics.accuracy'),
          rhythm: t('metrics.rhythm'),
          corrections: t('metrics.corrections'),
        }}
      />

      {/* Говорится измеренное, а не предполагаемое. Приложение видит
          сочетание высокой точности с долгими промежутками — и называет
          именно его, а причину оставляет человеку (ТЗ FR-EN-80). */}
      {level.accurateButSlow && (
        <p className="t2x-notice">{t('diagnosticResult.accurateButSlow')}</p>
      )}

      <section>
        <h3>{t('diagnosticResult.nextStep')}</h3>
        <p className="t2x-muted">{t(`diagnosticResult.recommend.${level.recommendation}`)}</p>

        {/* Оба пути видны всегда, отличаются только видом кнопки. Спрятать
            непорекомендованный значило бы решить за человека: тот, кому
            советуют тренировку, вправе поставить руки, а тот, кому советуют
            основы, — не считать себя обязанным их проходить. */}
        <div className="t2x-actions">
          <Button variant={advised === 'practice' ? 'primary' : 'secondary'} onClick={onContinue}>
            {t('diagnosticResult.continue.practice')}
          </Button>

          <Button
            variant={advised === 'fundamentals' ? 'primary' : 'secondary'}
            onClick={onStartFundamentals}
          >
            {t('diagnosticResult.continue.fundamentals')}
          </Button>

          <Button variant="ghost" onClick={onRetake}>
            {t('diagnosticResult.retake')}
          </Button>
        </div>

        <p className="t2x-muted t2x-legal">{t('diagnosticResult.notAVerdict')}</p>
      </section>

      {/* Разбор — после действия: он объясняет, а решение уже принято. */}
      {weakSpots.length > 0 && (
        <HeatMap
          caption={t('diagnosticResult.weakSpots')}
          cells={weakSpots.map((spot) => ({
            label: spot.unit.content,
            intensity: spot.severity,
          }))}
        />
      )}

      <details className="t2x-disclosure">
        <summary>{t('diagnosticResult.definitions')}</summary>
        <div className="t2x-disclosure__body">
          <p className="t2x-muted">{t('metrics.explain.wpm')}</p>
          <p className="t2x-muted">{t('metrics.explain.accuracy')}</p>
          <p className="t2x-muted">{t('metrics.explain.rhythm')}</p>
        </div>
      </details>
    </Card>
  );
}
